import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import type { Query } from 'nextjs-routes';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useMedia } from 'react-use';

import { MatchaAutoBanner } from '@/components/MatchaAutoBanner';
import { TokenPriceChart } from '@/components/TokenPriceChart';
import { LimitOrderDismissibleWarning } from '@/components/TransactionModule/Alerts/LimitOrderDismissibleWarning';
import { TransactionModuleContainer } from '@/components/TransactionModule/TransactionModuleContainer';
import type { TradeDirection } from '@/components/TransactionModule/types';
import { TradeEngineProvider } from '@/components/TransactionModule/useTradeEngine';
import {
  isSameQuery,
  navigateToTokenProfilePage,
  QueryOptions,
} from '@/components/TransactionModule/utils';

import {
  NATIVE_TOKEN_SYMBOL_PER_CHAIN,
  NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID,
  SUPPORTED_CHAIN_IDS,
} from '@/constants/chain';

import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';
import { useTokenResultsMultiChain } from '@/hooks/tokenQueries/useTokenResults';
import { useActiveToken } from '@/hooks/useActiveToken';
import { useAnnouncementBanner } from '@/hooks/useAnnouncementBanner';
import { useCheckoutMode, CheckoutModes } from '@/hooks/useCheckoutMode';
import { useCustomContent } from '@/hooks/useCustomContent';
import { useInitializeDefaultTokens } from '@/hooks/useInitializeDefaultTokens';
import { useSelectedTokens } from '@/hooks/useSelectedTokens';
import { useTokenInfo } from '@/hooks/useTokenInfo';
import { useZora } from '@/hooks/useZora';

import { dismissedLimitOrderFillInfoSelectors, useAppStore } from '@/store/app';

import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { Panel } from '@/ui/Panel';

import { isTokenBlockedByTokenList } from '@/utils/0x/token-blocking';
import { TokenBase, TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';
import { hexToRGB } from '@/utils/colors';
import { ChainAddress } from '@/utils/models';
import { areEquivalentTokens, areSameTokens, isTokenResultNativeAsset } from '@/utils/multichain';
import { routes } from '@/utils/routes';
import { TokenInfo } from '@/utils/token.types';
import { getTokenInfoFromQueryParameter } from '@/utils/url';
import { isZoraToken } from '@/utils/zora';

import { TokenProfileRelatedArticles } from './RelatedArticlesSection';
import { SankeyPreview } from './SankeyPreview';
import { TokenJsonLd } from './TokenJsonLd';
import { TokenProfileAbout } from './TokenProfileAbout';
import { TokenProfileFAQ } from './TokenProfileFAQ';
import { TokenProfileHeader } from './TokenProfileHeader';
import { TokenProfileLiquidityScore } from './TokenProfileLiquidityScore';
import { TokenProfileRelatedTokens } from './TokenProfileRelatedTokens';
import { TokenProfileSecurity } from './TokenProfileSecurityAudit';
import { ZoraProfileCoins } from './ZoraProfileCoins';

const TransactionModule = dynamic(
  () => import('@/components/TransactionModule').then((module) => module.TransactionModule),
  { ssr: false },
);

interface TokenProfileProps {
  activeToken: TokenInfo | undefined;
}

/**
 * Token profile content.
 */
export const TokenProfile = ({ activeToken: activeTokenProp }: TokenProfileProps) => {
  const [checkoutMode] = useCheckoutMode();
  const router = useRouter();
  const activeTokenFromQuery = useActiveToken();
  const { address: activeTokenAddress, chainId: activeTokenChainId } =
    activeTokenProp || activeTokenFromQuery || {};
  const { buyTokenChainId, buyTokenAddress, sellTokenChainId, sellTokenAddress } =
    useSelectedTokens(activeTokenFromQuery);

  // Writes the hook-resolved pair to the URL. Both sides are written so the
  // query stays self-consistent (a sell-only query would leave the buy side
  // empty and re-trigger the fill). Uses `replace` so the automatic fill never
  // adds a history entry - Back must leave the token page, not strip the
  // params and re-arm the fill.
  const syncUrlParameters = useCallback(
    (
      newBuyAddress?: ChainAddress,
      newBuyChain?: number,
      newSellAddress?: ChainAddress,
      newSellChain?: number,
    ) => {
      const tokenQuery: Query = {};
      if (newBuyAddress && newBuyChain) {
        tokenQuery.buyAddress = newBuyAddress;
        tokenQuery.buyChain = newBuyChain.toString();
      }
      if (newSellAddress && newSellChain) {
        tokenQuery.sellAddress = newSellAddress;
        tokenQuery.sellChain = newSellChain.toString();
      }

      const query = { ...router.query, ...tokenQuery };
      if (isSameQuery(query, router.query)) return;

      router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });
    },
    [router],
  );

  // Fill the empty sell side with the static default pair on the page token's chain.
  useInitializeDefaultTokens({
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    syncUrlParameters,
  });

  const isTabletOrDesktop = useMedia(breakpoints.tablet, false);
  const [autoBannerVisible, setAutoBannerVisible] = useState<boolean>(false);
  const { showBanner } = useAnnouncementBanner();
  const rightPanelRef = useRef<HTMLDivElement>(null);

  const { activeTokenInfo, inactiveTokenInfo, buyTokenInfo, sellTokenInfo } = useMemo(() => {
    const sellTokenInfo =
      sellTokenAddress && sellTokenChainId
        ? { address: sellTokenAddress, chainId: sellTokenChainId }
        : undefined;
    const buyTokenInfo =
      buyTokenAddress && buyTokenChainId
        ? { address: buyTokenAddress, chainId: buyTokenChainId }
        : undefined;
    return {
      activeTokenInfo: { address: activeTokenAddress, chainId: activeTokenChainId },
      inactiveTokenInfo:
        activeTokenAddress === sellTokenAddress && activeTokenChainId === sellTokenChainId
          ? buyTokenInfo
          : sellTokenInfo,
      buyTokenInfo,
      sellTokenInfo,
    };
  }, [
    activeTokenAddress,
    activeTokenChainId,
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
  ]);

  const { token: activeToken } = useTokenResult(activeTokenInfo?.address, activeTokenInfo?.chainId);
  const { token: inactiveToken } = useTokenResult(
    inactiveTokenInfo?.address,
    inactiveTokenInfo?.chainId,
  );
  const isTokenBlocked = isTokenBlockedByTokenList(activeToken);
  const { data: tokenInfo, isLoading: isTokenInfoLoading } = useTokenInfo(
    activeTokenInfo?.address,
    activeTokenInfo?.chainId,
  );

  const isZora = isZoraToken(activeToken);

  const { data: customContent, isLoading: isCustomContentLoading } = useCustomContent(
    activeTokenInfo?.address,
    activeTokenInfo?.chainId,
  );

  const { tokenContent, relatedTokens } = customContent?.result?.data ?? {};

  const { tokenAddresses, chainIds } = useMemo(() => {
    if (!relatedTokens) return { tokenAddresses: [], chainIds: [] };
    return relatedTokens.reduce<{ tokenAddresses: string[]; chainIds: number[] }>(
      (acc, token) => {
        if (SUPPORTED_CHAIN_IDS.includes(token.chainId)) {
          acc.tokenAddresses.push(token.address);
          if (!acc.chainIds.includes(token.chainId)) {
            acc.chainIds.push(token.chainId);
          }
        }
        return acc;
      },
      { tokenAddresses: [], chainIds: [] },
    );
  }, [relatedTokens]);

  const { isLoading: isLoadingRelatedTokens, tokenMap } = useTokenResultsMultiChain(
    tokenAddresses,
    chainIds,
  );

  // controls loading state extending custom content
  const isLoadingTokenPageData = !activeTokenInfo || isTokenInfoLoading;

  const enrichedRelatedTokens = useMemo(() => {
    if (!relatedTokens || !tokenMap) return [];

    return relatedTokens.map((token) => {
      const tokenData = tokenMap[token.chainId]?.[token.address.toLowerCase()];
      return {
        ...token,
        symbol: tokenData?.symbol,
      };
    });
  }, [relatedTokens, tokenMap]);

  const { dismissedLimitOrderFillInfo, setDismissedLimitOrderFillInfo } = useAppStore(
    dismissedLimitOrderFillInfoSelectors,
  );

  const handleActiveTokenAddressChange = useCallback(
    (selectedToken: TokenResult) => {
      const networkName =
        NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[selectedToken.chainId].toLowerCase();
      const contractAddressOrNativeSymbol = isTokenResultNativeAsset(selectedToken)
        ? NATIVE_TOKEN_SYMBOL_PER_CHAIN[selectedToken.chainId].toLowerCase()
        : selectedToken.address;

      const {
        query: {
          buyAddress: buyAddressQueryParam,
          buyChain: buyChainQueryParam,
          sellAddress: sellAddressQueryParam,
          sellChain: sellChainQueryParam,
        },
      } = router;

      const query: Query = {};
      if (buyAddressQueryParam && buyChainQueryParam && sellTokenAddress && sellTokenChainId) {
        query.sellAddress = sellTokenAddress;
        query.sellChain = sellTokenChainId.toString();
      }

      if (sellAddressQueryParam && sellChainQueryParam && buyTokenAddress && buyTokenChainId) {
        query.buyAddress = buyTokenAddress;
        query.buyChain = buyTokenChainId.toString();
      }

      router.push({
        pathname: routes.TOKEN_PROFILE,
        query: {
          ...router.query,
          ...query,
          chainId: networkName,
          contractAddresses: [contractAddressOrNativeSymbol],
        },
      });
    },
    [buyTokenAddress, buyTokenChainId, router, sellTokenAddress, sellTokenChainId],
  );

  const handleFlipSelectedTokens = useCallback(() => {
    const {
      query: { buyChain, buyAddress, sellChain, sellAddress },
    } = router;

    // if there is no buy or sell query param, there is only an active token defaulted to the buy token
    const flippedQuery =
      !buyChain && !buyAddress && !sellChain && !sellAddress
        ? { sellToken: activeToken }
        : {
            buyToken: getTokenInfoFromQueryParameter(sellAddress, sellChain),
            sellToken: getTokenInfoFromQueryParameter(buyAddress, buyChain),
          };

    navigateToTokenProfilePage(activeToken, flippedQuery);
  }, [activeToken, router]);

  const handleSelectPair = useCallback(
    (nextBuyToken: TokenInfo, nextSellToken: TokenInfo) => {
      const nextActiveToken =
        [nextBuyToken, nextSellToken].find((token) => areEquivalentTokens(activeToken, token)) ??
        activeToken;

      navigateToTokenProfilePage(nextActiveToken, {
        buyToken: nextBuyToken,
        sellToken: nextSellToken,
      });
    },
    [activeToken],
  );

  const handleSelectToken = useCallback(
    (selectedToken: TokenBase, side: TradeDirection) => {
      const isBuyToken = side === 'buy';
      const previousToken = isBuyToken ? buyTokenInfo : sellTokenInfo;
      const otherToken = isBuyToken ? sellTokenInfo : buyTokenInfo;

      if (side === 'sell') {
        setTimeout(() => {
          document.getElementById('sellAmount')?.focus();
        }, 0);
      }

      if (areSameTokens(selectedToken, otherToken)) {
        handleFlipSelectedTokens();
      } else if (previousToken && areEquivalentTokens(selectedToken, otherToken)) {
        handleSelectPair(
          isBuyToken ? selectedToken : previousToken,
          isBuyToken ? previousToken : selectedToken,
        );
      } else if (activeToken?.address === previousToken?.address) {
        // Current side is the page token — navigate to the newly selected token's profile
        const queryOptions: QueryOptions = {
          buyToken: undefined,
          sellToken: undefined,
        };
        if (isBuyToken) {
          if (!otherToken) {
            queryOptions.buyToken = selectedToken;
          } else {
            queryOptions.sellToken = otherToken;
          }
        } else {
          queryOptions.buyToken = otherToken;
          queryOptions.sellToken = selectedToken;
        }
        navigateToTokenProfilePage(selectedToken, queryOptions);
      } else {
        navigateToTokenProfilePage(
          activeToken,
          isBuyToken ? { buyToken: selectedToken } : { sellToken: selectedToken },
        );
      }
    },
    [activeToken, buyTokenInfo, handleFlipSelectedTokens, handleSelectPair, sellTokenInfo],
  );

  // Update top position of sticky right panel on window resize or right-panel
  // height change to make sure its content never overflows the window height
  useEffect(() => {
    if (rightPanelRef.current == null) {
      return;
    }

    const calculateRightPanelTopPosition = () => {
      const panelHeight = rightPanelRef.current?.getBoundingClientRect().height ?? 0;
      const bottomGap = 8;
      const defaultTop = showBanner ? 122 : 86;
      const processedTop = Math.min(defaultTop, window.innerHeight - panelHeight - bottomGap);
      document.body.style.setProperty(
        '--token-profile-right-panel-top',
        `${processedTop.toString()}px`,
      );
    };

    calculateRightPanelTopPosition();

    const onWindowResize = () => calculateRightPanelTopPosition();
    window.addEventListener('resize', onWindowResize);

    const resizeObserver = new ResizeObserver(calculateRightPanelTopPosition);
    resizeObserver.observe(rightPanelRef.current);

    return () => {
      window.removeEventListener('resize', onWindowResize);
      resizeObserver.disconnect();
    };
  }, [showBanner]);

  useEffect(() => {
    const style = (document.querySelector(':root') as HTMLElement).style;
    if (activeToken?.prominentColor) {
      const { r, g, b } = hexToRGB(activeToken.prominentColor);
      style.setProperty('--dynamic-bg-light', `rgba(${r},${g},${b},0.04)`);
      style.setProperty('--dynamic-bg-dark', `rgba(${r},${g},${b},0.06)`);
    }
  }, [activeToken]);

  const showLimitOrderFillInfo =
    checkoutMode === CheckoutModes.advanced && !dismissedLimitOrderFillInfo;

  const shouldShowSankeyPreview =
    checkoutMode === CheckoutModes.market &&
    sellTokenChainId === buyTokenChainId && // Sankey should only be displayed when both tokens are on the same chain
    isTabletOrDesktop;

  const zora = useZora({
    address: isZora ? activeToken?.address : undefined,
    chainId: isZora ? activeToken?.chainId : undefined,
  });

  const faqs = tokenContent?.faqs;

  return (
    <main>
      {activeToken && 'name' in activeToken && 'chainName' in activeToken && (
        <TokenJsonLd
          tokenName={activeToken.name}
          tokenSymbol={activeToken.symbol}
          tokenAddress={activeToken.address}
          chainId={activeToken.chainId}
          chainName={activeToken.chainName}
          tokenLogo={activeToken.logo}
          faqs={faqs}
          marketData={{
            priceChange24h: tokenInfo?.info?.market_data?.price_change_percentage_24h,
            volume24h: tokenInfo?.stats?.volume24h,
            marketCap: tokenInfo?.stats?.marketCap,
          }}
        />
      )}
      <TradeEngineProvider
        sellTokenChainId={sellTokenChainId}
        sellTokenAddress={sellTokenAddress}
        buyTokenChainId={buyTokenChainId}
        buyTokenAddress={buyTokenAddress}
      >
        <div
          className={classNames(
            'grid mx-auto pb-21 pt-18 tablet:pt-21 w-full max-w-[1276px] min-h-[100dvh] grid-cols-[100%]',
            "[grid-template-areas:'profileHeader''profileHeaderButtons''tokenInfo']",
            "tablet:[grid-template-areas:'profileHeader_tradeModule''tokenInfo_tradeModule']",
            '[body[data-show-banner=true]_&]:pt-28 tablet:[body[data-show-banner=true]_&]:pt-[122px]',
            'tablet:gap-x-3 tablet:gap-y-1 desktop:gap-y-1 tablet:grid-cols-[calc(100%-372px)_360px]',
            'desktop:grid-cols-[calc(100%-480px)_468px] desktop:grid-rows-[auto_1fr]',
          )}
        >
          <div className="flex flex-col pl-4 pb-0 tablet:pb-2 pr-4 tablet:pr-0">
            <Panel>
              <TokenProfileHeader
                activeToken={activeToken}
                displayName={tokenContent?.displayName}
                inactiveToken={inactiveToken}
                isLoading={isLoadingTokenPageData}
                onTokenSelect={handleActiveTokenAddressChange}
                zora={zora}
                tokenInfo={tokenInfo}
              />
            </Panel>
          </div>
          <div className="flex flex-col gap-3 pl-4 pb-24 tablet:pb-4 pr-4 tablet:pr-0 pt-3 tablet:pt-0 [grid-area:tokenInfo]">
            <Panel className="py-4">
              <TokenPriceChart
                token={activeToken}
                alternateToken={inactiveToken}
                showLabels={true}
                showVolume={true}
                showTimeframes={true}
                showTokenPrice={true}
                showMetricSelector={true}
                enableCandlestickView={true}
                autoRefresh={true}
                showTradeMarkers={true}
                height={320}
              />
            </Panel>
            <Panel>
              <TokenProfileAbout
                token={activeToken}
                data={tokenInfo?.info}
                isLoading={isTokenInfoLoading}
                tokenContent={tokenContent}
                zora={zora}
              />
            </Panel>
            {isZora && (
              <Panel>
                <ZoraProfileCoins zora={zora} />
              </Panel>
            )}
            <Panel>
              <TokenProfileSecurity activeToken={activeToken} isLoading={isLoadingTokenPageData} />
            </Panel>
            <Panel>
              <TokenProfileLiquidityScore
                activeToken={activeToken}
                topPools={tokenInfo?.topPools}
              />
            </Panel>
            {tokenContent && (
              <>
                {enrichedRelatedTokens?.length > 0 && tokenContent.category && (
                  <Panel>
                    {
                      <TokenProfileRelatedTokens
                        relatedTokens={enrichedRelatedTokens}
                        loading={isLoadingRelatedTokens}
                        issuer={activeToken?.issuer?.name}
                        showSummary={tokenContent.category.showSummary}
                        content={tokenContent.category.description}
                      />
                    }
                  </Panel>
                )}
                {!!tokenContent.faqs && tokenContent.faqs.length > 0 && (
                  <Panel>
                    <TokenProfileFAQ isLoading={isCustomContentLoading} data={tokenContent.faqs} />
                  </Panel>
                )}
                {!!tokenContent.relatedArticles && tokenContent.relatedArticles.length > 0 && (
                  <Panel className="overflow-hidden">
                    <TokenProfileRelatedArticles
                      relatedArticles={tokenContent.relatedArticles}
                      isLoading={isCustomContentLoading}
                    />
                  </Panel>
                )}
              </>
            )}
            {!isLoadingTokenPageData && (
              <div
                className={fontStyles({
                  variant: 'CAPTION',
                  className: 'text-token-gray-700 mt-3',
                })}
              >
                <p className="mb-3">
                  This content is based on information provided by the issuer, CoinGecko, and other
                  third-party information sources. 0x has not independently verified this
                  information, and does not guarantee its accuracy. 0x does not endorse this token
                  or any token; and this content does not provide advice on advisability or
                  investing in tokens or this token.
                </p>

                <p className="mb-8">
                  * The Liquidity Score is data that measure a token’s depth (in USD) across all DEX
                  markets at a snapshot in time. Automated scans like this one are not always
                  completely accurate and the measurement is stored for 5 minutes. A token with a
                  high Liquidity Score can become illiquid within the timespan of a single block.
                  The Liquidity Score is not legal or investment advice and should be considered
                  along with other factors. Always do your own research and consult multiple sources
                  of information.
                </p>
              </div>
            )}
          </div>
          <div className="pr-0 tablet:pr-4 [grid-area:tradeModule]">
            <div className="sticky top-[var(--token-profile-right-panel-top)]" ref={rightPanelRef}>
              <TransactionModuleContainer isTokenBlocked={isTokenBlocked}>
                <TransactionModule
                  autoBannerVisible={autoBannerVisible}
                  buyTokenChainId={buyTokenChainId}
                  buyTokenAddress={buyTokenAddress}
                  onFlipSelectedTokens={handleFlipSelectedTokens}
                  onSelectToken={handleSelectToken}
                  onSelectPair={handleSelectPair}
                  sellTokenChainId={sellTokenChainId}
                  sellTokenAddress={sellTokenAddress}
                  setAutoBannerVisible={setAutoBannerVisible}
                  className="absolute tablet:static"
                />
              </TransactionModuleContainer>

              {shouldShowSankeyPreview && (
                <div className="my-3">
                  <SankeyPreview
                    buyToken={
                      activeToken?.address === buyTokenAddress ? activeToken : inactiveToken
                    }
                    sellToken={
                      activeToken?.address === sellTokenAddress ? activeToken : inactiveToken
                    }
                    chainId={activeToken?.chainId || 1}
                  />
                </div>
              )}

              {showLimitOrderFillInfo && (
                <LimitOrderDismissibleWarning
                  onDismiss={() => setDismissedLimitOrderFillInfo(true)}
                />
              )}

              {autoBannerVisible &&
                sellTokenChainId &&
                isTabletOrDesktop &&
                checkoutMode === CheckoutModes.market && (
                  <MatchaAutoBanner
                    chainId={sellTokenChainId}
                    setAutoBannerVisible={setAutoBannerVisible}
                  />
                )}
            </div>
          </div>
        </div>
      </TradeEngineProvider>
    </main>
  );
};
